import { createContext, FC, ReactNode, useContext } from 'react'

/**
 * Optional side-channel for power-analytics events. When an embedder (e.g. the
 * Dossiers page) needs to react to viewer telemetry — without re-deriving the
 * rich payloads that only exist at the event source — it wraps the viewer in a
 * {@link PowerAnalyticsListenerProvider}; `usePowerAnalyticsBackend` then fans
 * each event out to that listener in addition to sending it to the backend.
 *
 * Properties are intentionally loose (`Record<string, unknown>`) so this file
 * carries no dependency on the event payload types and stays cycle-free; the
 * listener narrows by `eventName` and casts as needed.
 */
export type PowerAnalyticsListener = (
  eventName: string,
  properties: Record<string, unknown>
) => void

const PowerAnalyticsListenerContext = createContext<
  PowerAnalyticsListener | undefined
>(undefined)

export const PowerAnalyticsListenerProvider: FC<{
  listener: PowerAnalyticsListener
  children: ReactNode
}> = ({ listener, children }) => (
  <PowerAnalyticsListenerContext.Provider value={listener}>
    {children}
  </PowerAnalyticsListenerContext.Provider>
)

export const usePowerAnalyticsListener = ():
  | PowerAnalyticsListener
  | undefined => useContext(PowerAnalyticsListenerContext)
